import React from "react";
import { PageHero, SectionHeader, FadeUp } from "../../components/public/Section";
import { CheckCircle2, Target, Eye as EyeIcon, Award, Milestone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const TIMELINE = [
  { year: "1983", title: "Foundation", body: "GOS was established by a group of pioneering ophthalmologists in Ghaziabad." },
  { year: "1995", title: "First Annual Conference", body: "Launched the annual scientific conference that continues to this day." },
  { year: "2005", title: "CME Program Launched", body: "Structured continuing medical education program introduced." },
  { year: "2015", title: "Community Outreach", body: "Society-wide screening and awareness camps became a regular initiative." },
  { year: "2023", title: "40 Years of Service", body: "Celebrated four decades of contribution to eye care in the region." },
];

export default function About() {
  return (
    <div>
      <PageHero title="About the Society" subtitle="A professional home for ophthalmologists committed to clinical excellence, academic rigor and community service." breadcrumbs={["Home", "About"]} />
      <section className="section">
        <div className="container-page grid grid-cols-1 lg:grid-cols-3 gap-10">
          <FadeUp className="card-elev p-8">
            <div className="w-12 h-12 rounded-xl bg-brand-primary-light text-brand-primary flex items-center justify-center"><Target size={22} /></div>
            <h3 className="font-serif text-2xl font-bold mt-5">Our Mission</h3>
            <p className="text-brand-muted mt-3 leading-relaxed">Promote excellence in ophthalmology through education, research, ethical practice and community engagement.</p>
          </FadeUp>
          <FadeUp delay={0.1} className="card-elev p-8">
            <div className="w-12 h-12 rounded-xl bg-brand-primary-light text-brand-primary flex items-center justify-center"><EyeIcon size={22} /></div>
            <h3 className="font-serif text-2xl font-bold mt-5">Our Vision</h3>
            <p className="text-brand-muted mt-3 leading-relaxed">A future where every citizen has access to world-class, evidence-based eye care delivered by highly trained ophthalmologists.</p>
          </FadeUp>
          <FadeUp delay={0.2} className="card-elev p-8">
            <div className="w-12 h-12 rounded-xl bg-brand-primary-light text-brand-primary flex items-center justify-center"><Award size={22} /></div>
            <h3 className="font-serif text-2xl font-bold mt-5">Our Values</h3>
            <ul className="mt-3 space-y-2 text-brand-muted text-sm">
              {["Integrity", "Excellence", "Compassion", "Collaboration", "Innovation"].map((v) => (
                <li key={v} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-brand-accent" /> {v}</li>
              ))}
            </ul>
          </FadeUp>
        </div>
      </section>

      <section className="section-tight bg-white border-y border-brand-border">
        <div className="container-page">
          <SectionHeader eyebrow="Objectives" title="What we stand for" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              "Foster continuing medical education among ophthalmologists.",
              "Encourage clinical research and evidence-based practice.",
              "Facilitate collaboration between hospitals, academic institutions and practitioners.",
              "Promote community eye health awareness and preventive care.",
              "Uphold high ethical standards in medical practice.",
              "Support mentorship for residents, fellows and young ophthalmologists.",
            ].map((o) => (
              <FadeUp key={o} className="flex gap-3 items-start p-5 rounded-xl border border-brand-border bg-brand-bg">
                <CheckCircle2 className="text-brand-accent shrink-0" size={20} />
                <span className="text-brand-text">{o}</span>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-page">
          <SectionHeader eyebrow="Our Journey" title="Milestones through the years" />
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-brand-border" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <FadeUp key={t.year} className={`relative md:grid md:grid-cols-2 md:gap-10 ${i % 2 === 0 ? "" : "md:[&>div:first-child]:col-start-2"}`}>
                  <div className={`pl-14 md:pl-0 ${i % 2 === 0 ? "md:pr-10 md:text-right" : "md:pl-10 md:col-start-2"}`}>
                    <div className={`absolute left-0 md:left-1/2 top-1 w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center -translate-x-0 md:-translate-x-1/2 shadow-md`}>
                      <Milestone size={14} />
                    </div>
                    <div className="text-brand-primary font-serif text-2xl font-bold">{t.year}</div>
                    <div className="font-semibold text-brand-text mt-1">{t.title}</div>
                    <div className="text-brand-muted text-sm mt-2 max-w-md">{t.body}</div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
          <div className="text-center mt-14">
            <Link to="/executive-committee" className="btn-primary">View Organization Structure <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
